/* Brand skin for the Consentmo cookie bar.

   WHY THIS IS JAVASCRIPT AND NOT A STYLESHEET. The app renders the bar inside a
   shadow root on <csm-cookie-consent>. Shadow DOM is a style boundary: no rule
   in any theme stylesheet reaches inside it, at any specificity. The only way in
   from the theme is to put a <style> element inside the shadow root itself,
   which is all this file does. The root is open, so this needs no app access.

   WHAT IT DOES NOT TOUCH. Reject and Accept are styled identically and stay that
   way - same size, same colour, same weight, side by side. They arrive identical
   from the app, and under the EU consent rules this shop sells into, the reject
   control has to be as prominent as the accept one; quietly muting Reject while
   "tidying up" the bar would turn a styling pass into a compliance problem.
   Nothing here changes what the buttons do, what text they carry, or which
   cookies get set.

   SELECTORS, AND WHY THEY CARRY #csm-wrapper. The app ships two sets of class
   names on every element: hashed build names like .DFBP8UZ3LKbp5s7P5wQ0, which
   change whenever the app is rebuilt, and stable semantic ones like .cc-window,
   .cc-btn, .cc-deny. Only the stable ones are used.

   Two of the app's three stylesheets are loaded cross-origin from the Shopify
   CDN, so their rules cannot be read from script - but they still apply, and at
   least one of them carries !important on the mobile button layout and beat a
   bare .cc-compliance here. Every rule is therefore prefixed with the wrapper's
   id, which outranks a plain class whatever the opaque sheets contain.

   Worst case on an app update is that an id or class is renamed, the skin stops
   applying, and the bar reverts to the app's own look - never a broken banner.

   The colours are the same tokens as the frosted header, so the bar reads as
   part of the site rather than an app bolted onto it.
*/
(function () {
    'use strict';

    var STYLE_ID = 'dico-cookie-skin';

    var CSS = [
        /* The bar: frosted like the header, square, no drop shadow. A hairline
           rather than a shadow gives it an edge against the page without the
           lifted-card look the rest of the site does not use. */
        '#csm-wrapper .cc-window, #csm-wrapper .csm-cmp-bar {',
        '  background: rgba(255, 255, 255, .9) !important;',
        '  -webkit-backdrop-filter: blur(10px) !important;',
        '  backdrop-filter: blur(10px) !important;',
        '  border-radius: 0 !important;',
        '  box-shadow: none !important;',
        '  border-top: 1px solid rgba(0, 0, 0, .08) !important;',
        '  color: #000 !important;',
        '}',

        '#csm-wrapper .csm-cmp-body {',
        '  align-items: center !important;',
        '  gap: 28px !important;',
        '}',

        /* 16px body copy was larger than anything else on the site. */
        '#csm-wrapper .cc-message, #csm-wrapper .isense-cc-message {',
        '  font-size: 12px !important;',
        '  line-height: 1.65 !important;',
        '  color: #000 !important;',
        '  margin: 0 !important;',
        '}',

        '#csm-wrapper .cc-link, #csm-wrapper .isense-cc-link {',
        '  color: #000 !important;',
        '  text-decoration: underline !important;',
        '  text-underline-offset: 2px !important;',
        '}',

        '#csm-wrapper .cc-compliance {',
        '  gap: 10px !important;',
        '}',

        /* Uppercase and letter-spaced to match the nav and the add-to-cart
           button. One rule for both buttons, on purpose - see the header. */
        '#csm-wrapper .cc-btn, #csm-wrapper .csm-cc-btn {',
        '  background: #000 !important;',
        '  color: #fff !important;',
        '  border: 1px solid #000 !important;',
        '  border-radius: 0 !important;',
        '  font-size: 11px !important;',
        '  font-weight: 700 !important;',
        '  letter-spacing: .08em !important;',
        '  text-transform: uppercase !important;',
        '  padding: 12px 28px !important;',
        '  min-width: 130px !important;',
        '  white-space: nowrap !important;',
        '  transition: background .15s ease, color .15s ease !important;',
        '}',

        '#csm-wrapper .cc-btn:hover, #csm-wrapper .csm-cc-btn:hover {',
        '  background: #fff !important;',
        '  color: #000 !important;',
        '}',

        '#csm-wrapper .cc-btn:focus-visible, #csm-wrapper .csm-cc-btn:focus-visible {',
        '  outline: 2px solid #000 !important;',
        '  outline-offset: 2px !important;',
        '}',

        /* Phone. The app stacks the buttons in a column-reverse, which both put
           Accept above Reject and shrank each to its text width. They go back
           side by side, sharing the row equally, matching desktop - so neither
           is the bigger target and the order is the same on both. */
        '@media (max-width: 777px) {',
        '  #csm-wrapper .csm-cmp-body {',
        '    align-items: stretch !important;',
        '    gap: 16px !important;',
        '  }',
        '  #csm-wrapper .cc-compliance {',
        '    flex-direction: row !important;',
        '    align-self: stretch !important;',
        '    width: auto !important;',
        '  }',
        '  #csm-wrapper .cc-btn, #csm-wrapper .csm-cc-btn {',
        '    flex: 1 1 0 !important;',
        '    min-width: 0 !important;',
        '    width: auto !important;',
        '    padding: 13px 8px !important;',
        '  }',
        '  #csm-wrapper .cc-message, #csm-wrapper .isense-cc-message {',
        '    font-size: 11px !important;',
        '    text-align: left !important;',
        '  }',
        '}'
    ].join('\n');

    function paint(root) {
        if (!root || root.querySelector('#' + STYLE_ID)) { return; }
        var style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = CSS;
        /* Last child, so it also wins on order against the app's own stylesheets
           inside the same root. */
        root.appendChild(style);
    }

    var watched = null;

    function attach() {
        var host = document.querySelector('csm-cookie-consent');
        if (!host || !host.shadowRoot) { return false; }
        paint(host.shadowRoot);

        /* The app re-renders the bar when the customer opens preferences or
           reopens it later, which drops the style with everything else. */
        if (watched !== host.shadowRoot) {
            watched = host.shadowRoot;
            new MutationObserver(function () { paint(watched); })
                .observe(watched, { childList: true });
        }
        return true;
    }

    if (!attach()) {
        /* The app injects its element well after DOMContentLoaded. Watch for it,
           and give up after 30s rather than observing the document forever. */
        var obs = new MutationObserver(function () { if (attach()) { obs.disconnect(); } });
        obs.observe(document.documentElement, { childList: true, subtree: true });
        window.setTimeout(function () { obs.disconnect(); }, 30000);
    }
}());
